import { Component, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { map, Subscription, takeWhile, timer } from 'rxjs';
import { SecondToMmssPipe } from '../../pipes/second-to-mmss.pipe';
import { MiddlewareService } from '../../../core/services/middleware.service';
import { LoaderService } from '../../../core/services/loader.service';
import { EncryptionService } from '../../../core/services/encryption.service';
import { ModalType } from '../../../core/enums/modal-type.enum';
import { ApiResponse } from '../../../core/enums/api-response.enum';
import { apiURL } from '../../../../assets/data/api-url';
import { ModalService } from '../../../core/services/modal.service';
import { mobileIdPathEnum } from '../../../core/enums/mobileId-path.enum';

@Component({
  selector: 'app-mobile-auth-instruction',
  standalone: true,
  imports: [
    TranslateModule,
    SecondToMmssPipe
  ],
  templateUrl: './mobile-auth-instruction.component.html',
  styleUrl: './mobile-auth-instruction.component.scss'
})
export class MobileAuthInstructionComponent {
  @Input() mobileAuthTrackId!: any;
  @Input() civilId!: any;
  @Input() loginId!: any;
  @Input() mobileIdPath = mobileIdPathEnum.UPDATE_PROFILE;
  @Input() mobileIdAuthPath: string = '';
  timeoutId: any;
  authRetryIntervalMs = 5000; // 5 seconds
  authMaxDurationMs = 15 * 1000; // 15 seconds
  authenticationInitiatedTime!: number;
  resendCodeTime = -1;
  private countdownSubscription: Subscription | undefined;
  countDownLimit = 300;  // 5 minutes = 300 seconds

  constructor(
    public activeModal: NgbActiveModal,
    private _middlewareService: MiddlewareService,
    private loaderService: LoaderService,
    private encryptionService: EncryptionService,
    private commonModalService: ModalService,
    private translateService: TranslateService,
  ) { }

  ngOnInit(): void {
    this.startCountdown();
  }

  ngOnDestroy(): void {
    this.countdownSubscription?.unsubscribe();
  }

  startCountdown() {
    this.countdownSubscription?.unsubscribe();
    this.countdownSubscription = timer(0, 1000).pipe(
      map((elapsed) => this.countDownLimit - elapsed),
      takeWhile((remaining) => remaining >= 0)
    ).subscribe({
      next: (remainingTime) => {
        this.resendCodeTime = remainingTime;
      },
      complete: () => {
        this.resendCodeTime = 0; // Reset when completed
      },
      error: () => {
        this.resendCodeTime = 0;
      }
    });
  }

  onClickVerify() {
    this.authenticationInitiatedTime = Date.now();
    (this.mobileIdPath === mobileIdPathEnum.BUSINESS_AUTH ||
      this.mobileIdPath === mobileIdPathEnum.GOVERNMENT_AUTH) ?
      this.checkMobileAuthStatus() : this.checkFamilyMobileAuthStatus();
  }

  checkFamilyMobileAuthStatus() {
    if (this.mobileAuthTrackId) {
      const encryptedPayload = this.encryptionService.encryptData({
        trackId: this.mobileAuthTrackId,
        ...(this.mobileIdAuthPath && { mobileIdAuthPath: this.mobileIdAuthPath })
      });
      this._middlewareService
        .callPostMiddleware(apiURL.getMobileIdAuthResponse, { payload: encryptedPayload })
        .subscribe({
          next: (response: any) => {
            if (response?.code === ApiResponse.OK) {
              if (response?.result?.Result == 'success') {
                this.loaderService.showLoader.set(false);
                this.clearTimeoutAction();
                this.activeModal.close({ message: 'success', response: response?.result });
              } else if (response?.result?.Result == 'fail') {
                const timeDifference = Date.now() - this.authenticationInitiatedTime;
                if (timeDifference <= this.authMaxDurationMs) {
                  this.timeoutId = setTimeout(() => {
                    this.checkFamilyMobileAuthStatus();
                  }, this.authRetryIntervalMs);
                } else {
                  this.clearTimeoutAction();
                  this.loaderService.showLoader.set(false);
                  // this.activeModal.close('error');
                  // this.commonModalService.commonAlertModal("ERROR_MESSAGES.DEFUALT_ERROR", ModalType.WARNING);
                }
              } else if (response?.result?.Result == 'User is not eligible') {
                this.clearTimeoutAction();
                this.activeModal.close('User is not eligible');
              } else {
                this.clearTimeoutAction();
                this.activeModal.close('error');
                this.commonModalService.commonAlertModal("CREATE_VISA.CIVIL_ID_INFO.CIVIL_ID_NOT_MATCHED", ModalType.WARNING);
              }
            }
          },
          error: (error: any) => {
            this.clearTimeoutAction();
            this.activeModal.close('error');
            if (error.status == 409) {
              this.commonModalService.commonAlertModal("CREATE_VISA.CIVIL_ID_INFO.CIVIL_ID_AUTHENTICATION_CONFLICT", ModalType.WARNING)
            }
            else {
              if (error?.error?.message.includes('User already exists')) {
                this.commonModalService.commonAlertModal('ERROR_MESSAGES.USER_ALREADY_EXISTS', ModalType.WARNING);
              }
              else {
                this.commonModalService.commonAlertModal("ERROR_MESSAGES.DEFUALT_ERROR", ModalType.WARNING);
              }
            }
          },
        });
    }
  }

  checkMobileAuthStatus() {
    if (this.mobileAuthTrackId) {
      const encryptedPayload = this.encryptionService.encryptData({
        commercialLoginId: this.loginId,
        notificationId: this.mobileAuthTrackId
      });
      let statusCheckApiUrl = this.mobileIdPath === mobileIdPathEnum.BUSINESS_AUTH ?
        apiURL.checkCommercialAuthResponse : apiURL.mobileIdAuthStatusGov;
      this._middlewareService
        .callPostMiddleware(statusCheckApiUrl, { payload: encryptedPayload })
        .subscribe({
          next: (response: any) => {
            if (response?.code === ApiResponse.OK) {
              this.loaderService.showLoader.set(false);
              this.clearTimeoutAction();
              this.activeModal.close({ message: 'success', response: response?.result });
            } else if (response?.code === ApiResponse.SOMETHING_WRONG) {
              const timeDifference = Date.now() - this.authenticationInitiatedTime;
              if (timeDifference <= this.authMaxDurationMs) {
                this.timeoutId = setTimeout(() => {
                  this.checkMobileAuthStatus();
                }, this.authRetryIntervalMs);
              } else {
                this.clearTimeoutAction();
                this.loaderService.showLoader.set(false);
                // this.activeModal.close('error');
                // this.commonModalService.commonAlertModal("ERROR_MESSAGES.DEFUALT_ERROR", ModalType.WARNING);
              }
            } else {
              this.clearTimeoutAction();
              this.activeModal.close('error');
              this.commonModalService.commonAlertModal("ERROR_MESSAGES.DEFUALT_ERROR", ModalType.WARNING);
            }

          },
          error: (error: any) => {
            this.clearTimeoutAction();
            this.activeModal.close('error');
            error.status == 409 ?
              this.commonModalService.commonAlertModal("CREATE_VISA.CIVIL_ID_INFO.CIVIL_ID_AUTHENTICATION_CONFLICT", ModalType.WARNING)
              :
              this.commonModalService.commonAlertModal("ERROR_MESSAGES.DEFUALT_ERROR", ModalType.WARNING);
          },
        });
    }
  }

  clearTimeoutAction() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  resendMobileAuth() {
    this.mobileAuthTrackId = null;
    this.clearTimeoutAction();
    (
      this.mobileIdPath === mobileIdPathEnum.BUSINESS_AUTH ||
      this.mobileIdPath === mobileIdPathEnum.GOVERNMENT_AUTH
    ) ? this.resendWorkMobileAuth() : this.resendFamilyMobileAuth();
  }

  resendFamilyMobileAuth() {
    if (this.civilId) {
      const encryptedPayload = this.encryptionService.encryptData({
        civilId: this.civilId,
        mobileIdPath: this.mobileIdPath
      });
      this._middlewareService
        .callPostMiddleware(apiURL.mobileIdAuth, { payload: encryptedPayload })
        .subscribe({
          next: (response: any) => {
            if (response?.code === ApiResponse.OK) {
              if (response?.result?.code === ApiResponse.ERROR) {
                this.commonModalService.commonAlertModal(this.translateService.currentLang === 'ar' ? response?.result?.arabicMessage : response?.result?.message, ModalType.WARNING);
                return;
              }
              if (response?.result?.Result == 'success') {
                this.mobileAuthTrackId = response?.result?.TrackId;
                this.startCountdown();
              } else if (response?.result?.Result == 'fail') {
                this.activeModal.close('error');
                this.commonModalService.commonAlertModal("CREATE_VISA.CIVIL_ID_INFO.CIVIL_ID_AUTHENTICATION_REJECTED", ModalType.WARNING);
              } else if (response?.result?.Result == 'User is not eligible') {
                this.activeModal.close('User is not eligible');
              } else {
                this.activeModal.close('error');
                this.commonModalService.commonAlertModal("CREATE_VISA.CIVIL_ID_INFO.CIVIL_ID_NOT_MATCHED", ModalType.WARNING);
              }
            }
          },
          error: (error: any) => {
            this.activeModal.close('error');
            error.status == 409 ?
              this.commonModalService.commonAlertModal("CREATE_VISA.CIVIL_ID_INFO.CIVIL_ID_AUTHENTICATION_CONFLICT", ModalType.WARNING)
              :
              this.commonModalService.commonAlertModal("ERROR_MESSAGES.DEFUALT_ERROR", ModalType.WARNING);
          },
        });
    }
  }

  resendWorkMobileAuth() {
    if (this.loginId) {
      let initiateMobileAuthApiUrl = this.mobileIdPath === mobileIdPathEnum.BUSINESS_AUTH ?
        apiURL.initiateCommercialAuth : apiURL.mobileIdAuthInitGov;
      const encryptedPayload = this.encryptionService.encryptData({
        commercialLoginId: this.loginId
      });
      this._middlewareService
        .callPostMiddleware(initiateMobileAuthApiUrl, { payload: encryptedPayload })
        .subscribe({
          next: (response: any) => {
            if (response?.code === ApiResponse.OK) {
              if (response?.result?.notification && response?.result?.civilId) {
                const notificationObj = JSON.parse(response?.result?.notification);
                if (notificationObj.error) {
                  this.activeModal.close('error');
                  this.commonModalService.commonAlertModal(notificationObj?.data, ModalType.FAIL);
                } else {
                  this.mobileAuthTrackId = notificationObj?.data;
                  this.startCountdown();
                }
              } else {
                this.activeModal.close('error');
                this.commonModalService.commonAlertModal("BUSINESS_SIGN_IN.NO_ACCOUNT_ERROR_MESSAGE", ModalType.FAIL);
              }
            }
          },
          error: (error: any) => {
            this.activeModal.close('error');
            this.commonModalService.commonAlertModal("BUSINESS_SIGN_IN.NO_ACCOUNT_ERROR_MESSAGE", ModalType.FAIL);
            //  this.commonModalService.commonAlertModal("ERROR_MESSAGES.DEFUALT_ERROR", ModalType.WARNING);
          },
        });
    }
  }
}
